<template>
  <div class="equipment-account-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('equipmentAccountDetail.page.title')" :subtitle="$t('equipmentAccountDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack()">
          {{ $t('equipmentAccountDetail.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-printer" @click="_printEquipmentDetail()">
          {{ $t('equipmentAccountDetail.print') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) 设备信息面板 -->
    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('equipmentAccountDetail.title') }}</span>
        <ds-status-tag size="sm" type="info" :label="equipmentAccountDetail.stateName || '-'" :text="equipmentAccountDetail.stateName" />
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.machineName') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.machineName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.machineCode') }}</span>
          <span class="ds-info-grid__value ds-num">{{ equipmentAccountDetail.machineCode || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.levelName') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.levelName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.stateName') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.stateName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.brand') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.brand || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.model') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.model || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.locationDetail') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.locationDetail || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.firstEnableTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ equipmentAccountDetail.firstEnableTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.warrantyDeadline') }}</span>
          <span class="ds-info-grid__value ds-num">{{ equipmentAccountDetail.warrantyDeadline || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.usefulLife') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.usefulLife || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.purchasePrice') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(equipmentAccountDetail.purchasePrice) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.netWorth') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(equipmentAccountDetail.netWorth) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.useUserName') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.useUserName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.chargeUseName') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.chargeUseName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('equipmentAccountDetail.remark') }}</span>
          <span class="ds-info-grid__value">{{ equipmentAccountDetail.remark || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- (3)(4)(6) 日志 Toolbar + DataTable -->
    <div class="ds-panel">
      <ds-toolbar :total="equipmentAccountDetail.elogs ? equipmentAccountDetail.elogs.length : 0" :show-density="false" :show-refresh="false" :show-column-setting="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('equipmentAccountDetail.logTitle') }}</span>
        </template>
      </ds-toolbar>
      <ds-data-table :data="equipmentAccountDetail.elogs" border :empty-description="$t('equipmentAccountDetail.page.empty')">
        <el-table-column type="index" :label="$t('equipmentAccountDetail.serialNumber')" width="80" align="center" />
        <el-table-column prop="operatingCode" :label="$t('equipmentAccountDetail.operatingCode')" align="center" width="140" />
        <el-table-column prop="operatingDescriptor" :label="$t('equipmentAccountDetail.operatingDescriptor')" align="center" min-width="160" />
        <el-table-column prop="createTime" :label="$t('equipmentAccountDetail.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('equipmentAccountDetail.remark')" align="center" min-width="160" />
      </ds-data-table>
    </div>

    <view-image ref="viewImage" />
  </div>
</template>

<script>
import { getEquipmentAccountDetail, getEquipmentLogs } from '@/api/machine/equipmentAccountDetailApi'
import ViewImage from '@/components/system/viewImage'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'EquipmentAccountDetailList',
  components: {
    ViewImage,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag
  },
  data() {
    return {
      equipmentAccountDetail: {
        machineId: '',
        machineName: '',
        machineCode: '',
        brand: '',
        model: '',
        locationDetail: '',
        firstEnableTime: '',
        warrantyDeadline: '',
        usefulLife: '',
        importanceLevel: '',
        state: '',
        stateName: '',
        levelName: '',
        purchasePrice: '',
        netWorth: '',
        useOrgId: '',
        useOrgName: '',
        useUserId: '',
        useUserName: '',
        useUseTel: '',
        chargeOrgId: '',
        chargeOrgName: '',
        chargeOrgTel: '',
        chargeUseId: '',
        chargeUseName: '',
        remark: '',
        elogs: []
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    const machineId = this.$route.query.machineId
    if (!machineId) {
      this.$message.error(this.$t('equipmentAccountDetail.illegalOperation'))
      this.$router.push('/machine/equipmentAccount')
      return
    }
    this.equipmentAccountDetail.machineId = machineId
    this._listEquipmentDetail()
  },
  methods: {
    formatMoney(v) { return formatMoney(v) },
    async _listEquipmentDetail() {
      try {
        const params = {
          page: 1,
          row: 1,
          communityId: this.communityId,
          machineId: this.equipmentAccountDetail.machineId
        }
        const { data } = await getEquipmentAccountDetail(params)
        if (data.length < 1) {
          this.$message.error(this.$t('equipmentAccountDetail.dataError'))
          this.$router.push('/machine/equipmentAccount')
          return
        }
        this.equipmentAccountDetail = { ...this.equipmentAccountDetail, ...data[0] }
        this._loadEquipmentLogs()
      } catch (error) {
        console.error('Failed to fetch equipment detail:', error)
      }
    },
    async _loadEquipmentLogs() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.communityId,
          machineId: this.equipmentAccountDetail.machineId
        }
        const { data } = await getEquipmentLogs(params)
        this.equipmentAccountDetail.elogs = data
      } catch (error) {
        console.error('Failed to fetch equipment logs:', error)
      }
    },
    _goBack() {
      this.$router.go(-1)
    },
    openFile(photo) {
      this.$refs.viewImage.open(photo.url)
    },
    _printEquipmentDetail() {
      window.open(`/#/views/resource/printEquipmentAccountLabel?machineId=${this.equipmentAccountDetail.machineId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.equipment-account-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-lg);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__value {
    font-size: var(--ds-font-size-md);
    color: var(--ds-gray-800);
    word-break: break-all;
  }
}
</style>
